import { Controller } from "@hotwired/stimulus"
import { disableBackgroundInert, enableBackgroundInert, trapFocus, releaseFocusTrap, getFocusableElements } from "../utils/FocusTrap"

const CONTENT_SELECTOR = '.doc-image-modal-content'
const CLOSE_BUTTON_SELECTOR = '.doc-image-modal-close'
const EXPAND_BUTTON_SELECTOR = '.doc-image-expand'
const IMAGE_BUTTON_SELECTOR = '.responsive-image[role="button"]'
const OPEN_CLASS = 'doc-image-modal--open'
const FOCUS_TRAP_CAPTURE = true

const TRIGGER_SELECTORS = {
  expandButton: EXPAND_BUTTON_SELECTOR,
  imageButton: IMAGE_BUTTON_SELECTOR,
}

export default class extends Controller {
  static targets = ["modal"]
  static values = {
    uid: String
  }

  connect() {
    this.handleEscape = this.handleEscape.bind(this)
    this.handleModalClick = this.handleModalClick.bind(this)
    this.handleSingleFocusTab = this.handleSingleFocusTab.bind(this)
    this.inertedElements = []

    if (this.hasModalTarget) {
      this.modalElement = this.modalTarget
      this.portalModal()
      this.modalElement.addEventListener('click', this.handleModalClick)
    }
  }

  openWithKey(event) {
    if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return

    event.preventDefault()
    this.open(event)
  }

  open(event) {
    event.preventDefault()
    event.stopPropagation()

    this.rememberTrigger(event.currentTarget)

    if (!this.modalElement) return

    this.modalElement.style.display = 'flex'
    this.modalElement.classList.add(OPEN_CLASS)
    this.modalElement.setAttribute('aria-hidden', 'false')
    document.body.style.overflow = 'hidden'
    this.inertedElements = enableBackgroundInert(this.modalElement)

    document.addEventListener('keydown', this.handleEscape)
    document.addEventListener('keydown', this.handleSingleFocusTab, FOCUS_TRAP_CAPTURE)

    trapFocus(this.modalElement, {
      previousFocusElement: this.lastTrigger,
      initialFocusSelector: CLOSE_BUTTON_SELECTOR,
      skipInitialFocus: true,
    })

    this.focusInitialControl()
  }

  rememberTrigger(element) {
    this.lastTrigger = element

    if (element?.classList?.contains('doc-image-expand')) {
      this.lastTriggerRole = 'expandButton'
      return
    }

    if (element?.getAttribute('role') === 'button') {
      this.lastTriggerRole = 'imageButton'
      return
    }

    this.lastTriggerRole = 'expandButton'
  }

  resolveTriggerElement() {
    if (
      this.lastTrigger instanceof HTMLElement &&
      this.lastTrigger.isConnected &&
      this.element.contains(this.lastTrigger)
    ) {
      return this.lastTrigger
    }

    if (!this.element) return null

    const selector = TRIGGER_SELECTORS[this.lastTriggerRole] || EXPAND_BUTTON_SELECTOR
    return this.element.querySelector(selector)
  }

  restoreTriggerFocus() {
    const trigger = this.resolveTriggerElement()
    if (!trigger || typeof trigger.focus !== 'function') return

    const focusOptions = { preventScroll: true }

    const attemptFocus = () => {
      if (this.isOpen()) return
      trigger.focus(focusOptions)
    }

    attemptFocus()

    requestAnimationFrame(() => {
      attemptFocus()
      if (document.activeElement !== trigger) {
        requestAnimationFrame(attemptFocus)
      }
    })
  }

  focusInitialControl() {
    const closeButton = this.modalElement.querySelector(CLOSE_BUTTON_SELECTOR)
    if (!closeButton || typeof closeButton.focus !== 'function') return

    const focusOptions = { preventScroll: true, focusVisible: true }
    closeButton.focus(focusOptions)
    if (document.activeElement === closeButton) return

    requestAnimationFrame(() => {
      if (!this.isOpen()) return
      closeButton.focus(focusOptions)
    })
  }

  close(event) {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }

    if (!this.modalElement || !this.isOpen()) return

    releaseFocusTrap(this.modalElement, { restoreFocus: false })
    this.modalElement.style.display = 'none'
    this.modalElement.classList.remove(OPEN_CLASS)
    this.modalElement.setAttribute('aria-hidden', 'true')
    document.body.style.overflow = ''

    document.removeEventListener('keydown', this.handleEscape)
    document.removeEventListener('keydown', this.handleSingleFocusTab, FOCUS_TRAP_CAPTURE)

    disableBackgroundInert(this.inertedElements)
    this.inertedElements = []
    this.restoreTriggerFocus()

    this.lastTrigger = null
    this.lastTriggerRole = null
  }

  isOpen() {
    if (!this.modalElement) return false

    return (
      this.modalElement.classList.contains(OPEN_CLASS) &&
      this.modalElement.style.display !== 'none'
    )
  }

  handleSingleFocusTab(event) {
    if (event.key !== 'Tab' || !this.modalElement) return
    if (!this.isOpen()) return

    const focusable = getFocusableElements(this.modalElement)
    if (focusable.length !== 1) return
    if (document.activeElement !== focusable[0]) return

    event.preventDefault()
    event.stopImmediatePropagation()
  }

  handleEscape(event) {
    if (event.key !== 'Escape' || !this.modalElement) return
    if (!this.isOpen()) return

    event.preventDefault()
    this.close()
  }

  handleModalClick(event) {
    if (event.target.closest(CLOSE_BUTTON_SELECTOR)) {
      this.close(event)
      return
    }

    if (!event.target.closest(CONTENT_SELECTOR)) {
      this.close(event)
    }
  }

  disconnect() {
    if (this.isOpen()) {
      this.close()
    }

    document.removeEventListener('keydown', this.handleEscape)
    document.removeEventListener('keydown', this.handleSingleFocusTab, FOCUS_TRAP_CAPTURE)

    if (this.modalElement) {
      this.modalElement.removeEventListener('click', this.handleModalClick)
      document.body.style.overflow = ''
      disableBackgroundInert(this.inertedElements)
      this.inertedElements = []
      releaseFocusTrap(this.modalElement, { restoreFocus: false })
      this.restoreModal()
    }
  }

  portalModal() {
    if (!this.modalElement || this.modalElement.dataset.portaled === 'true') return

    this.modalHome = document.createComment('doc-image-modal-home')
    this.modalElement.before(this.modalHome)
    document.body.appendChild(this.modalElement)
    this.modalElement.dataset.portaled = 'true'
  }

  restoreModal() {
    if (!this.modalElement || !this.modalHome) return

    this.modalHome.replaceWith(this.modalElement)
    delete this.modalElement.dataset.portaled
    this.modalHome = null
  }
}
